import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AppShell from "../components/AppShell";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "../components/ui/dialog";
import { Heart, Clock, Plus, Trash2, MessageCircle, ShieldCheck, BookLock, ExternalLink, Backpack, DoorOpen } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { useContent } from "../hooks/useContent";
import T from "../components/T";
import { lightTap } from "../lib/haptics";

function ContactCard({ c, onDelete, onAlert }) {
  const { t } = useTranslation();
  return (
    <div className="rounded-3xl bg-card border border-border p-5" data-testid={`contact-${c.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-serif text-xl">{c.name}</div>
          <div className="text-sm text-foreground/60">{c.relationship || t("safety.trusted_default")}</div>
          {c.phone && <div className="text-sm text-foreground/75 mt-1">{c.phone}</div>}
          {c.code_word && (
            <div className="text-xs mt-2 inline-block px-2 py-0.5 rounded-full bg-brand/10 text-brand">
              {t("safety.code_label")} {c.code_word}
            </div>
          )}
        </div>
        <button data-testid={`contact-delete-${c.id}`} onClick={() => onDelete(c.id)} className="text-foreground/40 hover:text-destructive">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
      <div className="mt-4 grid grid-cols-2 gap-2">
        <Button data-testid={`alert-callme-${c.id}`} onClick={() => onAlert(c.id, "call_me")}
          variant="outline" className="rounded-full text-xs">{t("safety.alert_callme")}</Button>
        <Button data-testid={`alert-needhelp-${c.id}`} onClick={() => onAlert(c.id, "need_help")}
          className="rounded-full text-xs bg-brand hover:bg-brand/90 text-white">{t("safety.alert_needhelp")}</Button>
        <Button data-testid={`alert-checkin-${c.id}`} onClick={() => onAlert(c.id, "check_tomorrow")}
          variant="outline" className="rounded-full text-xs">{t("safety.alert_checkin")}</Button>
        <Button data-testid={`alert-fake-${c.id}`} onClick={() => onAlert(c.id, "fake_emergency")}
          variant="outline" className="rounded-full text-xs">{t("safety.alert_fake")}</Button>
      </div>
    </div>
  );
}

export default function Safety() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { HOTLINES } = useContent();
  const [contacts, setContacts] = useState([]);
  const [checkins, setCheckins] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", relationship: "", code_word: "" });
  const [minutes, setMinutes] = useState(60);
  const [note, setNote] = useState("");

  // Scroll to the hotlines section when navigated with `#hotlines` (used by
  // Leaving Safely Checklist's "Need immediate help?" button).
  //
  // The Safety page inserts a stream of content ABOVE the hotlines heading
  // during mount: trusted-contacts data, check-in cards, and the Resources
  // shortcut grid all render staggered as async data resolves. A single
  // scroll (even smooth or delayed) locks its target y at fire time and
  // stops tracking, so any later insertion above pushes hotlines back down.
  //
  // Strategy: re-invoke `scrollIntoView` on every observed body-size change
  // for up to 1500 ms, or until the user interacts (wheel/touch). This
  // keeps the anchor pinned while layout settles, and stays out of the
  // user's way as soon as they take control.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.location.hash !== "#hotlines") return;

    let cancelled = false;
    const doScroll = () => {
      if (cancelled) return;
      const el = document.getElementById("hotlines");
      if (el) el.scrollIntoView({ behavior: "auto", block: "start" });
    };
    doScroll();

    const ro = new ResizeObserver(doScroll);
    ro.observe(document.body);

    const stop = () => {
      cancelled = true;
      ro.disconnect();
    };
    const timer = setTimeout(stop, 1500);
    window.addEventListener("wheel", stop, { once: true, passive: true });
    window.addEventListener("touchstart", stop, { once: true, passive: true });

    return () => {
      clearTimeout(timer);
      stop();
      window.removeEventListener("wheel", stop);
      window.removeEventListener("touchstart", stop);
    };
  }, []);

  const refresh = async () => {
    const [c, t2] = await Promise.all([api.get("/trusted-contacts"), api.get("/checkins")]);
    setContacts(c.data);
    setCheckins(t2.data);
  };
  useEffect(() => { refresh(); }, []);

  const addContact = async (e) => {
    e.preventDefault();
    if (!form.name) return;
    try {
      await api.post("/trusted-contacts", form);
      setForm({ name: "", phone: "", relationship: "", code_word: "" });
      setOpen(false);
      toast.success(t("safety.contact_added"));
      lightTap();
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("safety.contact_error"));
    }
  };

  const deleteContact = async (id) => {
    await api.delete(`/trusted-contacts/${id}`);
    toast(t("safety.contact_removed"));
    refresh();
  };

  const sendAlert = async (id, kind) => {
    await api.post(`/trusted-contacts/${id}/alert`, { kind });
    toast.success(t("safety.alert_sent"), { duration: 4000 });
  };

  const startCheckin = async () => {
    await api.post("/checkins", { minutes: Number(minutes), note });
    setNote("");
    toast.success(t("safety.checkin_started", { n: minutes }));
    refresh();
  };

  const confirmSafe = async (id) => { await api.post(`/checkins/${id}/safe`); refresh(); };
  const cancelCi  = async (id) => { await api.post(`/checkins/${id}/cancel`); refresh(); };

  const pending = checkins.find((c) => c.status === "pending");

  return (
    <AppShell>
      <div className="px-8 pt-16 pb-6" data-testid="page-safety">
        <p className="font-script text-2xl text-brand">{t("safety.kicker")}</p>
        <h1 className="font-serif text-4xl leading-tight">
          <T i18nKey="safety.h" />
        </h1>
        <p className="mt-3 text-foreground/70 leading-relaxed">{t("safety.intro")}</p>
      </div>

      {/* Trusted Circle */}
      <section className="px-8">
        <div className="flex items-center justify-between">
          <h2 className="font-serif text-2xl flex items-center gap-2"><Heart className="w-5 h-5 text-brand" /> {t("safety.trusted_circle")}</h2>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-contact-btn" size="sm" className="rounded-full bg-brand hover:bg-brand/90 text-white">
                <Plus className="w-4 h-4 mr-1" /> {t("safety.add")}
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-3xl max-w-sm" aria-describedby="add-contact-desc">
              <DialogHeader><DialogTitle className="font-serif text-2xl">{t("safety.add_trusted_h")}</DialogTitle></DialogHeader>
              <p id="add-contact-desc" className="sr-only">{t("safety.add_trusted_desc")}</p>
              <form onSubmit={addContact} className="space-y-3 mt-2">
                <div>
                  <Label className="text-xs uppercase tracking-wider">{t("safety.name")}</Label>
                  <Input data-testid="contact-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-12 rounded-2xl" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider">{t("safety.phone")}</Label>
                  <Input data-testid="contact-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="h-12 rounded-2xl" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider">{t("safety.relationship")}</Label>
                  <Input data-testid="contact-rel-input" placeholder={t("safety.relationship_placeholder")} value={form.relationship} onChange={(e) => setForm({ ...form, relationship: e.target.value })} className="h-12 rounded-2xl" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider">{t("safety.code_word")}</Label>
                  <Input data-testid="contact-code-input" placeholder={t("safety.code_word_placeholder")} value={form.code_word} onChange={(e) => setForm({ ...form, code_word: e.target.value })} className="h-12 rounded-2xl" />
                </div>
                <DialogFooter>
                  <Button data-testid="contact-save-btn" type="submit" className="rounded-full bg-brand hover:bg-brand/90 text-white">{t("common.save")}</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <p className="mt-2 text-sm text-foreground/55">{t("safety.up_to_5")}</p>

        <div className="mt-4 space-y-3">
          {contacts.length === 0 && (
            <div className="rounded-3xl bg-card border border-dashed border-border p-6 text-center text-foreground/60" data-testid="contacts-empty">
              {t("safety.no_contacts")}
            </div>
          )}
          {contacts.map((c) => (
            <ContactCard key={c.id} c={c} onDelete={deleteContact} onAlert={sendAlert} />
          ))}
        </div>
      </section>

      {/* Check-in timer */}
      <section className="px-8 mt-10">
        <h2 className="font-serif text-2xl flex items-center gap-2"><Clock className="w-5 h-5 text-brand" /> {t("safety.checkin_h")}</h2>
        <div className="mt-3 rounded-3xl bg-card border border-border p-5">
          {pending ? (
            <div data-testid="checkin-pending">
              <p className="text-sm text-foreground/60">{t("safety.checkin_pending")}</p>
              <p className="font-serif text-xl mt-1">{t("safety.checkin_expires", { time: new Date(pending.expires_at).toLocaleTimeString() })}</p>
              {pending.note && <p className="text-sm text-foreground/70 mt-1">{pending.note}</p>}
              <div className="mt-3 flex gap-2">
                <Button data-testid="checkin-safe-btn" onClick={() => confirmSafe(pending.id)} className="rounded-full bg-brand hover:bg-brand/90 text-white">{t("safety.checkin_safe")}</Button>
                <Button data-testid="checkin-cancel-btn" onClick={() => cancelCi(pending.id)} variant="outline" className="rounded-full">{t("common.cancel")}</Button>
              </div>
            </div>
          ) : (
            <div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider">{t("safety.minutes")}</Label>
                  <Input data-testid="checkin-minutes-input" type="number" min={1} max={720} value={minutes} onChange={(e) => setMinutes(e.target.value)} className="h-12 rounded-2xl" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider">{t("safety.note")}</Label>
                  <Input data-testid="checkin-note-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder={t("safety.note_placeholder")} className="h-12 rounded-2xl" />
                </div>
              </div>
              <Button data-testid="checkin-start-btn" onClick={startCheckin} className="mt-3 w-full rounded-full bg-brand hover:bg-brand/90 text-white h-12">{t("safety.start_checkin")}</Button>
              <p className="mt-2 text-xs text-foreground/55">{t("safety.missed_warning")}</p>
            </div>
          )}
        </div>

        {checkins.length > 0 && (
          <div className="mt-3 space-y-2">
            {checkins.slice(0, 5).map((c) => (
              <div key={c.id} className="text-sm flex items-center justify-between rounded-2xl bg-card border border-border px-4 py-2" data-testid={`checkin-row-${c.id}`}>
                <span className="text-foreground/70">{new Date(c.created_at).toLocaleString()}</span>
                <span className={`text-xs uppercase tracking-wide ${
                  c.status === "safe" ? "text-emerald-600" : c.status === "missed" ? "text-rose-600" : "text-foreground/60"
                }`}>{c.status}</span>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Resources */}
      <section className="px-8 mt-10">
        <h2 className="font-serif text-2xl flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-brand" /> {t("safety.resources")}</h2>
        <div className="mt-3 grid grid-cols-2 gap-3">
          <button data-testid="shortcut-incident-log" onClick={() => navigate("/incident-log")} className="rounded-3xl bg-card border border-border p-5 text-left hover:border-brand/50 transition-all min-h-[120px]">
            <BookLock className="w-5 h-5 text-brand" />
            <div className="font-serif text-lg mt-2">{t("safety.incident_log")}</div>
            <p className="text-xs text-foreground/60 mt-1">{t("safety.incident_log_desc")}</p>
          </button>
          <button data-testid="shortcut-before-go-back" onClick={() => navigate("/before-you-go-back")} className="rounded-3xl bg-card border border-border p-5 text-left hover:border-brand/50 transition-all min-h-[120px]">
            <MessageCircle className="w-5 h-5 text-brand" />
            <div className="font-serif text-lg mt-2">{t("safety.before_go_back")}</div>
            <p className="text-xs text-foreground/60 mt-1">{t("safety.before_go_back_desc")}</p>
          </button>
          <button data-testid="shortcut-emergency-kit" onClick={() => navigate("/emergency-escape-kit")} className="rounded-3xl bg-card border border-border p-5 text-left hover:border-brand/50 transition-all min-h-[120px]">
            <Backpack className="w-5 h-5 text-brand" />
            <div className="font-serif text-lg mt-2">{t("safety.emergency_kit")}</div>
            <p className="text-xs text-foreground/60 mt-1">{t("safety.emergency_kit_desc")}</p>
          </button>
          <button data-testid="shortcut-leaving-safely" onClick={() => navigate("/leaving-safely-checklist")} className="rounded-3xl bg-card border border-border p-5 text-left hover:border-brand/50 transition-all min-h-[120px]">
            <DoorOpen className="w-5 h-5 text-brand" />
            <div className="font-serif text-lg mt-2">{t("safety.leaving_safely")}</div>
            <p className="text-xs text-foreground/60 mt-1">{t("safety.leaving_safely_desc")}</p>
          </button>
        </div>

        <h3 id="hotlines" className="mt-6 text-xs uppercase tracking-widest text-foreground/55 scroll-mt-20">{t("safety.hotlines")}</h3>
        <div className="mt-2 rounded-3xl bg-card border border-border divide-y divide-border overflow-hidden">
          {HOTLINES.map((h, i) => (
            <a key={i} href={h.web} target="_blank" rel="noopener noreferrer"
              data-testid={`hotline-${i}`}
              className="flex items-center justify-between p-4 hover:bg-brand/5">
              <div>
                <div className="font-medium">{h.name}</div>
                <div className="text-sm text-foreground/65">{h.phone}{h.sms ? ` · ${h.sms}` : ""}</div>
              </div>
              <ExternalLink className="w-4 h-4 text-foreground/40" />
            </a>
          ))}
        </div>
      </section>

      <div className="h-40" />
    </AppShell>
  );
}
